/**
 * OpenAPI spec builder for auto-generated entity + standalone action routes.
 *
 * Walks `RegisteredModule[]` from the engine store and produces the OpenAPI
 * `paths` object for every route that `entity.routes.ts` and `action.routes.ts`
 * mount at runtime. Manual routes (declared via `customRoutes` / `@hono/zod-openapi`)
 * are handled by Hono's `app.getOpenAPIDocument()` and are NOT emitted here.
 */

import type {
  ActionDefinition,
  EntityDefinition,
  FieldDefinition,
  LocalizedString,
  SingleEntityDefinition,
} from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import type { RegisteredModule } from '../../kernel/engine/module-store.js'
import { fieldsToOpenApiObject, type OpenApiSchema } from './field-schema.js'
import { inferEntityRoutePath } from './utils.js'

type OpenApiOperation = Record<string, unknown>
type OpenApiPathItem = Record<string, OpenApiOperation>
type OpenApiPaths = Record<string, OpenApiPathItem>

export interface EntitiesOpenApiResult {
  paths: OpenApiPaths
}

/**
 * Build OpenAPI `paths` for every auto-generated route in a list of modules.
 */
export function buildEntitiesOpenApi(modules: RegisteredModule[]): EntitiesOpenApiResult {
  const paths: OpenApiPaths = {}

  for (const mod of modules) {
    if (mod._disableEndpoints) continue
    const modulePrefix = mod.routePrefix ?? `/${mod.name}`
    const base = `/api/v1${modulePrefix.startsWith('/') ? modulePrefix : `/${modulePrefix}`}`

    const tag = mod.name.charAt(0).toUpperCase() + mod.name.slice(1)
    for (const def of mod.definitions ?? []) {
      if ((def as { expose?: boolean }).expose === false) continue
      addEntityPaths(paths, base, def, tag)
    }
    for (const action of mod.actions ?? []) {
      addStandaloneActionPaths(paths, base, action, tag)
    }
  }

  return { paths }
}

// ---------------------------------------------------------------------------
// Entity-level paths
// ---------------------------------------------------------------------------

function addEntityPaths(
  paths: OpenApiPaths,
  base: string,
  def: EntityDefinition,
  tag: string,
): void {
  const route = inferEntityRoutePath(def)
  const prefix = joinPath(base, route)
  const type = def.type ?? 'collection'
  const isSingleton =
    (type === 'single' || (type as string) === 'config') &&
    !('scopeField' in def && (def as SingleEntityDefinition).scopeField)

  const sensitive =
    (def.casl as { sensitiveFields?: string[] } | undefined)?.sensitiveFields ?? []
  const fields = (def as { fields?: Record<string, FieldDefinition> }).fields ?? {}
  const writeSchema = fieldsToOpenApiObject(fields, { excludeFields: sensitive })
  const readSchema = fieldsToOpenApiObject(fields)
  const label = resolveLabel(def.label)

  // Entity actions — registered BEFORE CRUD in runtime to avoid /:id capture.
  const entityActions = (def as { actions?: ActionDefinition[] }).actions ?? []
  for (const action of entityActions) addEntityAction(paths, prefix, action, tag)

  // Tree/dag-specific routes
  if (type === 'tree' || type === 'dag') {
    paths[`${prefix}/tree`] = {
      get: simpleOp(tag, `Get ${label} tree`, [treeQueryParams()].flat()),
    }
    paths[`${prefix}/roots`] = { get: simpleOp(tag, `Get ${label} roots`) }
    paths[`${prefix}/{id}/move`] = {
      post: {
        tags: [tag],
        summary: `Move ${label} node`,
        parameters: [idParam()],
        requestBody: jsonBody({
          type: 'object',
          properties: { parentId: { type: 'string' } },
        }),
        responses: { 200: { description: 'OK' } },
      },
    }
    for (const sub of ['ancestors', 'descendants', 'children'] as const) {
      paths[`${prefix}/{id}/${sub}`] = {
        get: {
          tags: [tag],
          summary: `Get ${label} ${sub}`,
          parameters: [idParam()],
          responses: { 200: { description: 'OK' } },
        },
      }
    }
    if (type === 'dag') {
      paths[`${prefix}/{id}/parents`] = {
        get: {
          tags: [tag],
          summary: `Get ${label} parents`,
          parameters: [idParam()],
          responses: { 200: { description: 'OK' } },
        },
        put: {
          tags: [tag],
          summary: `Set ${label} parents`,
          parameters: [idParam()],
          requestBody: jsonBody({
            type: 'object',
            properties: { parentIds: { type: 'array', items: { type: 'string' } } },
          }),
          responses: { 204: { description: 'Updated' } },
        },
        post: {
          tags: [tag],
          summary: `Add ${label} parent`,
          parameters: [idParam()],
          requestBody: jsonBody({
            type: 'object',
            properties: { parentId: { type: 'string' } },
          }),
          responses: { 204: { description: 'Added' } },
        },
      }
      paths[`${prefix}/{id}/parents/{parentId}`] = {
        delete: {
          tags: [tag],
          summary: `Remove ${label} parent`,
          parameters: [idParam(), pathParam('parentId')],
          responses: { 204: { description: 'Removed' } },
        },
      }
    }
  }

  // Bulk (BEFORE /:id in runtime; here order of the map doesn't matter for OpenAPI)
  paths[`${prefix}/bulk`] = {
    post: bulkOp(tag, 'create', label),
    put: bulkOp(tag, 'update', label),
    delete: bulkOp(tag, 'delete', label),
  }
  paths[`${prefix}/count`] = {
    get: {
      tags: [tag],
      summary: `Count ${label}`,
      responses: {
        200: {
          description: 'OK',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: { count: { type: 'integer' } },
              },
            },
          },
        },
      },
    },
  }

  if (type === 'computed') {
    paths[`${prefix}/recompute`] = {
      post: {
        tags: [tag],
        summary: `Recompute ${label}`,
        responses: { 200: { description: 'OK' } },
      },
    }
  }

  if (isSingleton) {
    paths[prefix] = {
      get: {
        tags: [tag],
        summary: `Get ${label}`,
        responses: okResponse(readSchema),
      },
      put: {
        tags: [tag],
        summary: `Update ${label}`,
        requestBody: jsonBody(writeSchema),
        responses: okResponse(readSchema),
      },
    }
  } else {
    paths[prefix] = {
      get: {
        tags: [tag],
        summary: `List ${label}`,
        parameters: paginationParams(),
        responses: { 200: listResponse(readSchema) },
      },
      post: {
        tags: [tag],
        summary: `Create ${label}`,
        requestBody: jsonBody(writeSchema),
        responses: {
          201: {
            description: 'Created',
            content: { 'application/json': { schema: readSchema } },
          },
        },
      },
    }
    paths[`${prefix}/{id}`] = {
      get: {
        tags: [tag],
        summary: `Get ${label}`,
        parameters: [idParam()],
        responses: okResponse(readSchema),
      },
      put: {
        tags: [tag],
        summary: `Update ${label}`,
        parameters: [idParam()],
        requestBody: jsonBody(writeSchema),
        responses: okResponse(readSchema),
      },
      delete: {
        tags: [tag],
        summary: `Delete ${label}`,
        parameters: [idParam()],
        responses: { 204: { description: 'Deleted' } },
      },
    }
  }
}

function addEntityAction(
  paths: OpenApiPaths,
  prefix: string,
  action: ActionDefinition,
  tag: string,
): void {
  const scope = action.scope ?? 'row'
  if (scope === 'module') return // module-scope actions live on the parent router
  const method = (action.method ?? 'POST').toLowerCase()
  const actionPath =
    scope === 'row' ? `${prefix}/${action.key}/{id}` : `${prefix}/${action.key}`
  const parameters = scope === 'row' ? [idParam()] : []
  const inputSchema = action.input ? fieldsToOpenApiObject(action.input) : undefined
  const summary = resolveLabel(action.label) || `Action ${action.key}`

  const op: OpenApiOperation = {
    tags: [tag],
    summary,
    responses: { 200: { description: 'OK' } },
  }
  if (parameters.length > 0) op['parameters'] = parameters
  if (inputSchema) op['requestBody'] = jsonBody(inputSchema)

  paths[actionPath] = { [method]: op }

  if (action.batch) {
    const streamPath =
      scope === 'row'
        ? `${prefix}/${action.key}/stream/{id}`
        : `${prefix}/${action.key}/stream`
    const streamOp: OpenApiOperation = {
      tags: [tag],
      summary: `${summary} (SSE stream)`,
      responses: { 200: { description: 'SSE stream' } },
    }
    if (parameters.length > 0) streamOp['parameters'] = parameters
    paths[streamPath] = { get: streamOp }
  }
}

function addStandaloneActionPaths(
  paths: OpenApiPaths,
  base: string,
  action: ActionDefinition,
  tag: string,
): void {
  const method = (action.method ?? 'POST').toLowerCase()
  const inputSchema = action.input ? fieldsToOpenApiObject(action.input) : undefined
  const summary = resolveLabel(action.label) || `Action ${action.key}`

  const op: OpenApiOperation = {
    tags: [tag],
    summary,
    responses: { 200: { description: 'OK' } },
  }
  if (inputSchema) op['requestBody'] = jsonBody(inputSchema)
  paths[`${base}/${action.key}`] = { [method]: op }

  if (action.batch) {
    paths[`${base}/${action.key}/stream`] = {
      get: {
        tags: [tag],
        summary: `${summary} (SSE stream)`,
        responses: { 200: { description: 'SSE stream' } },
      },
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function joinPath(base: string, route: string): string {
  const combined = route === '/' ? base : `${base}${route}`
  const normalized = combined.replace(/\/$/, '')
  return normalized.length > 0 ? normalized : '/'
}

function idParam(): Record<string, unknown> {
  return {
    name: 'id',
    in: 'path',
    required: true,
    schema: { type: 'string' },
  }
}

function pathParam(name: string): Record<string, unknown> {
  return {
    name,
    in: 'path',
    required: true,
    schema: { type: 'string' },
  }
}

function paginationParams(): Array<Record<string, unknown>> {
  return [
    { name: 'page', in: 'query', schema: { type: 'integer' } },
    { name: 'limit', in: 'query', schema: { type: 'integer' } },
    { name: 'sort', in: 'query', schema: { type: 'string' } },
    { name: 'q', in: 'query', schema: { type: 'string' } },
  ]
}

function treeQueryParams(): Array<Record<string, unknown>> {
  return [
    { name: 'rootId', in: 'query', schema: { type: 'string' } },
    { name: 'maxDepth', in: 'query', schema: { type: 'integer' } },
  ]
}

function jsonBody(schema: OpenApiSchema): Record<string, unknown> {
  return {
    required: true,
    content: { 'application/json': { schema } },
  }
}

function okResponse(schema: OpenApiSchema): Record<string, unknown> {
  return {
    200: {
      description: 'OK',
      content: { 'application/json': { schema } },
    },
  }
}

function listResponse(itemSchema: OpenApiSchema): Record<string, unknown> {
  return {
    description: 'Paginated list',
    content: {
      'application/json': {
        schema: {
          type: 'object',
          properties: {
            items: { type: 'array', items: itemSchema },
            total: { type: 'integer' },
            page: { type: 'integer' },
            limit: { type: 'integer' },
            totalPages: { type: 'integer' },
            hasNext: { type: 'boolean' },
          },
        },
      },
    },
  }
}

function bulkOp(tag: string, verb: 'create' | 'update' | 'delete', label: string): OpenApiOperation {
  return {
    tags: [tag],
    summary: `Bulk ${verb} ${label}`,
    responses: { 200: { description: 'OK' } },
  }
}

function simpleOp(
  tag: string,
  summary: string,
  parameters: Array<Record<string, unknown>> = [],
): OpenApiOperation {
  const op: OpenApiOperation = {
    tags: [tag],
    summary,
    responses: { 200: { description: 'OK' } },
  }
  if (parameters.length > 0) op['parameters'] = parameters
  return op
}

function resolveLabel(label: LocalizedString | string | undefined): string {
  if (!label) return ''
  if (typeof label === 'string') return label
  return resolveLocalized(label, 'en')
}
